import { useState, useEffect } from "react";
import {
  Search,
  Filter,
  MoreVertical,
  Mail,
  GraduationCap,
  UserX,
  X,
  RotateCcw,
  Key,
  Activity,
  Smartphone
} from "lucide-react";
import { Button } from "@/components/admin/ui/Button";
import { Input } from "@/components/admin/ui/Input";
import { Badge } from "@/components/admin/ui/Badge";
import { Avatar } from "@/components/admin/ui/Avatar";
import { useToast } from "@/components/admin/ui/Toast";

export function Students() {
  const [studentList, setStudentList] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selected, setSelected] = useState<number[]>([]);
  const [search, setSearch] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [classFilter, setClassFilter] = useState("");
  const [groupFilter, setGroupFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalStudents, setTotalStudents] = useState(0);
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const { showToast } = useToast();

  // Modals States
  const [editingStudent, setEditingStudent] = useState<any | null>(null);
  const [resetPasswordStudent, setResetPasswordStudent] = useState<any | null>(null);
  const [activeLogStudent, setActiveLogStudent] = useState<any | null>(null);

  // Edit fields states
  const [editName, setEditName] = useState("");
  const [editMobile, setEditMobile] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editClass, setEditClass] = useState("");
  const [editGroup, setEditGroup] = useState("");
  const [editOptional, setEditOptional] = useState("");
  const [editInstitution, setEditInstitution] = useState("");
  const [editBoard, setEditBoard] = useState("");
  const [editCareerGoal, setEditCareerGoal] = useState("");
  const [editDreamUniversity, setEditDreamUniversity] = useState("");
  const [editDob, setEditDob] = useState("");
  const [editGender, setEditGender] = useState("");
  const [editAddress, setEditAddress] = useState("");
  const [editSubscription, setEditSubscription] = useState("");
  const [editEnrolledCourses, setEditEnrolledCourses] = useState("");

  // Password reset fields
  const [newPassword, setNewPassword] = useState("");

  // Activity Log fields
  const [activeLogData, setActiveLogData] = useState<any | null>(null);
  const [activeLogLoading, setActiveLogLoading] = useState(false);
  const [activeLogTab, setActiveLogTab] = useState<"overview" | "courses" | "quizzes" | "exams">("overview");

  const ajaxUrl =
    (window as any).shLmsAdmin?.ajax_url ||
    (window as any).hubInstructor?.ajax_url ||
    (window as any).ajaxurl;
  const adminNonce =
    (window as any).shLmsAdmin?.nonce ||
    (window as any).chorchaAdmin?.nonce ||
    (window as any).ajaxurl;

  const fetchStudents = () => {
    if (!ajaxUrl || !(window as any).jQuery) return;

    setIsLoading(true);
    (window as any).jQuery.ajax({
      url: ajaxUrl,
      method: "POST",
      data: {
        action: "sh_admin_get_students",
        nonce: adminNonce,
        search: search,
        class_filter: classFilter,
        group_filter: groupFilter,
        page: currentPage,
        limit: 15
      },
      success: (res: any) => {
        setIsLoading(false);
        if (res.success) {
          setStudentList(res.data.students);
          setTotalPages(res.data.pages);
          setTotalStudents(res.data.total);
        } else {
          showToast(res.data || "Failed to load students.", "error");
        }
      },
      error: () => {
        setIsLoading(false);
        showToast("Error loading students.", "error");
      }
    });
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchStudents();
    }, 450);

    return () => clearTimeout(delayDebounceFn);
  }, [search]);

  useEffect(() => {
    fetchStudents();
  }, [classFilter, groupFilter, currentPage]);

  const toggleAll = () => {
    if (selected.length === studentList.length) {
      setSelected([]);
    } else {
      setSelected(studentList.map((s) => s.id));
    }
  };

  const toggleOne = (id: number) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleToggleBlock = (student: any) => {
    if (!ajaxUrl || !(window as any).jQuery) return;
    
    const actionMessage = student.is_blocked ? "unblock" : "block";
    if (!confirm(`Are you sure you want to ${actionMessage} ${student.name}?`)) return;

    (window as any).jQuery.ajax({
      url: ajaxUrl,
      method: "POST",
      data: {
        action: "sh_admin_toggle_block_user",
        nonce: adminNonce,
        user_id: student.id
      },
      success: (res: any) => {
        if (res.success) {
          showToast(res.data.message || `Successfully ${actionMessage}ed student.`, "success");
          fetchStudents();
        } else {
          showToast(res.data || "Failed to change block status.", "error");
        }
      },
      error: () => {
        showToast("Error updating block status.", "error");
      }
    });
  };

  const openEditModal = (student: any) => {
    setEditingStudent(student);
    setEditName(student.name || "");
    setEditMobile(student.mobile || "");
    setEditEmail(student.email || "");
    setEditClass(student.class || "");
    setEditGroup(student.group || "");
    setEditOptional(student.optional || "none");
    setEditInstitution(student.institution || "");
    setEditBoard(student.board || "");
    setEditCareerGoal(student.career_goal || "");
    setEditDreamUniversity(student.dream_university || "");
    setEditDob(student.dob || "");
    setEditGender(student.gender || "");
    setEditAddress(student.address || "");
    setEditSubscription(student.subscription_tier || "free");
    setEditEnrolledCourses(student.courses?.join(", ") || "");
    setOpenMenuId(null);
  };

  const handleUpdateProfile = (e: React.FormEvent) => {
    e.preventDefault();
    if (!ajaxUrl || !(window as any).jQuery || !editingStudent) return;

    (window as any).jQuery.ajax({
      url: ajaxUrl,
      method: "POST",
      data: {
        action: "sh_admin_update_student_profile",
        nonce: adminNonce,
        user_id: editingStudent.id,
        display_name: editName,
        mobile: editMobile,
        email: editEmail,
        class: editClass,
        group: editGroup,
        optional: editOptional,
        institution: editInstitution,
        board: editBoard,
        career_goal: editCareerGoal,
        dream_university: editDreamUniversity,
        dob: editDob,
        gender: editGender,
        address: editAddress,
        subscription_tier: editSubscription,
        enrolled_courses: editEnrolledCourses
      },
      success: (res: any) => {
        if (res.success) {
          showToast(res.data || "Profile updated successfully.", "success");
          setEditingStudent(null);
          fetchStudents();
        } else {
          showToast(res.data || "Failed to update profile.", "error");
        }
      },
      error: () => {
        showToast("Network error updating profile.", "error");
      }
    });
  };

  const openResetPasswordModal = (student: any) => {
    setResetPasswordStudent(student);
    setNewPassword("");
    setOpenMenuId(null);
  };

  const handleResetPassword = (e: React.FormEvent) => {
    e.preventDefault();
    if (!ajaxUrl || !(window as any).jQuery || !resetPasswordStudent || !newPassword) return;

    (window as any).jQuery.ajax({
      url: ajaxUrl,
      method: "POST",
      data: {
        action: "sh_admin_set_student_password",
        nonce: adminNonce,
        user_id: resetPasswordStudent.id,
        password: newPassword
      },
      success: (res: any) => {
        if (res.success) {
          showToast("Password updated successfully.", "success");
          setResetPasswordStudent(null);
        } else {
          showToast(res.data || "Failed to update password.", "error");
        }
      },
      error: () => {
        showToast("Network error updating password.", "error");
      }
    });
  };

  const openActivityLogModal = (student: any) => {
    setActiveLogStudent(student);
    setActiveLogTab("overview");
    setActiveLogData(null);
    setActiveLogLoading(true);
    setOpenMenuId(null);

    if (!ajaxUrl || !(window as any).jQuery) return;

    (window as any).jQuery.ajax({
      url: ajaxUrl,
      method: "POST",
      data: {
        action: "sh_admin_get_student_activity",
        nonce: adminNonce,
        user_id: student.id
      },
      success: (res: any) => {
        setActiveLogLoading(false);
        if (res.success) {
          setActiveLogData(res.data);
        } else {
          showToast(res.data || "Failed to load student activity.", "error");
          setActiveLogStudent(null);
        }
      },
      error: () => {
        setActiveLogLoading(false);
        showToast("Network error loading student activity.", "error");
        setActiveLogStudent(null);
      }
    });
  };

  const handleResetProgress = (student: any) => {
    if (!confirm(`Are you sure you want to reset ${student.name}'s progress? This action is irreversible.`)) return;
    if (!ajaxUrl || !adminNonce || !(window as any).jQuery) return;

    (window as any).jQuery.ajax({
      url: ajaxUrl,
      method: "POST",
      data: {
        action: "sh_instructor_reset_student_progress",
        security: adminNonce,
        student_id: student.id
      },
      success: (res: any) => {
        if (res.success) {
          showToast(`Successfully reset progress for ${student.name}.`, "success");
        } else {
          showToast(res.data?.message || "Failed to reset student progress.", "error");
        }
      },
      error: () => {
        showToast("Error resetting student progress.", "error");
      }
    });
  };

  const translateClassBengali = (val: string) => {
    const map: Record<string, string> = {
      "class-6": "ষষ্ঠ শ্রেণী",
      "class-7": "সপ্তম শ্রেণী",
      "class-8": "অষ্টম শ্রেণী",
      "class-9": "নবম শ্রেণী",
      "class-10": "দশম শ্রেণী",
      "hsc-1": "এইচএসসি ১ম বর্ষ",
      "hsc-2": "এইচএসসি ২য় বর্ষ",
      "ssc-2027": "এসএসসি ২০২৭",
      "ssc-2028": "এসএসসি ২০২৮",
      "hsc-2026": "এইচএসসি ২০২৬",
      "hsc-2027": "এইচএসসি ২০২৭"
    };
    return map[val] || val;
  };

  const translateGroupBengali = (val: string) => {
    const map: Record<string, string> = {
      "science": "বিজ্ঞান",
      "humanities": "মানবিক",
      "commerce": "ব্যবসায় শিক্ষা"
    };
    return map[val] || val;
  };

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-black text-gray-800">
            Student Management
          </h1>
          <p className="text-xs text-gray-500 mt-1">
            Total {totalStudents} registered students in the system
          </p>
        </div>
      </div>

      {/* Table & Filtering */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden transition-all">
        {/* Search & Filter Trigger */}
        <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <Input
            placeholder="Search Students by Name, Mobile or Email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leftIcon={<Search className="h-4 w-4 text-gray-400" />}
            containerClassName="w-full sm:w-96"
          />
          <div className="flex items-center gap-3">
            <Button
              variant={filtersOpen ? "primary" : "outline"}
              size="sm"
              leftIcon={<Filter className="h-4 w-4" />}
              onClick={() => setFiltersOpen(!filtersOpen)}
              className="rounded-xl font-bold"
            >
              Filters
            </Button>
            {selected.length > 0 && (
              <span className="text-xs text-gray-500 font-bold bg-gray-100 px-3 py-1.5 rounded-xl">
                {selected.length} Selected
              </span>
            )}
          </div>
        </div>

        {/* Dynamic Filters */}
        {filtersOpen && (
          <div className="p-4 bg-gray-50/50 border-b border-gray-100 grid grid-cols-1 sm:grid-cols-2 gap-4 items-center animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="flex flex-col gap-1.5 text-left">
              <span className="text-[10px] font-black text-gray-400 uppercase tracking-wider">Class / Batch</span>
              <select
                value={classFilter}
                onChange={(e) => setClassFilter(e.target.value)}
                className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 font-bold focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
              >
                <option value="">All Classes</option>
                <option value="class-6">Class 6 (ষষ্ঠ শ্রেণী)</option>
                <option value="class-7">Class 7 (সপ্তম শ্রেণী)</option>
                <option value="class-8">Class 8 (অষ্টম শ্রেণী)</option>
                <option value="class-9">Class 9 (নবম শ্রেণী)</option>
                <option value="class-10">Class 10 (দশম শ্রেণী)</option>
                <option value="hsc-1">HSC 1st Year (এইচএসসি ১ম বর্ষ)</option>
                <option value="hsc-2">HSC 2nd Year (এইচএসসি ২য় বর্ষ)</option>
                <option value="ssc-2027">SSC 2027 (এসএসসি ২০২৭)</option>
                <option value="ssc-2028">SSC 2028 (এসএসসি ২০২৮)</option>
                <option value="hsc-2026">HSC 2026 (এইচএসসি ২০২৬)</option>
                <option value="hsc-2027">HSC 2027 (এইচএসসি ২০২৭)</option>
              </select>
            </div>
            <div className="flex flex-col gap-1.5 text-left">
              <span className="text-[10px] font-black text-gray-400 uppercase tracking-wider">Group</span>
              <select
                value={groupFilter}
                onChange={(e) => setGroupFilter(e.target.value)}
                className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 font-bold focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
              >
                <option value="">All Groups</option>
                <option value="science">Science (বিজ্ঞান)</option>
                <option value="humanities">Humanities (মানবিক)</option>
                <option value="commerce">Commerce (ব্যবসায় শিক্ষা)</option>
              </select>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm border-collapse">
            <thead className="bg-gray-50/75 border-b border-gray-100 text-gray-500">
              <tr>
                <th className="px-5 py-4 w-10">
                  <input
                    type="checkbox"
                    checked={
                      selected.length === studentList.length &&
                      studentList.length > 0
                    }
                    onChange={toggleAll}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500/30"
                  />
                </th>
                <th className="px-5 py-4 font-black text-xs uppercase tracking-wider">Student Profile</th>
                <th className="px-5 py-4 font-black text-xs uppercase tracking-wider">Academic details</th>
                <th className="px-5 py-4 font-black text-xs uppercase tracking-wider">Subscription & Status</th>
                <th className="px-5 py-4 font-black text-xs uppercase tracking-wider">Registration Date</th>
                <th className="px-5 py-4 font-black text-xs uppercase tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <span className="animate-spin border-4 border-indigo-500 border-t-transparent rounded-full w-8 h-8"></span>
                      <span className="text-xs text-gray-400 font-bold">Loading students...</span>
                    </div>
                  </td>
                </tr>
              ) : studentList.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center text-gray-400 text-xs font-bold">
                    No students found.
                  </td>
                </tr>
              ) : (
                studentList.map((student) => (
                  <tr
                    key={student.id}
                    className="hover:bg-gray-50/50 transition-colors"
                  >
                    <td className="px-5 py-4">
                      <input
                        type="checkbox"
                        checked={selected.includes(student.id)}
                        onChange={() => toggleOne(student.id)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500/30"
                      />
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3.5 min-w-[240px]">
                        <Avatar src={student.avatar} name={student.name} className="h-10 w-10 rounded-2xl border border-gray-100" />
                        <div className="text-left">
                          <p className="font-black text-gray-800 text-sm">
                            {student.name}
                          </p>
                          <p className="text-xs text-gray-400 flex items-center gap-1.5 mt-0.5">
                            <Mail className="h-3 w-3" /> {student.email}
                          </p>
                          <p className="text-xs text-gray-400 flex items-center gap-1.5 mt-0.5">
                            <Smartphone className="h-3 w-3" /> {student.mobile}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="text-xs space-y-1 text-left">
                        {student.class && (
                          <div className="flex items-center gap-1">
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-indigo-500"></span>
                            <span className="font-bold text-gray-700">{translateClassBengali(student.class)}</span>
                          </div>
                        )}
                        {student.group && (
                          <div className="text-gray-400 ml-2.5">{translateGroupBengali(student.group)}</div>
                        )}
                        {student.institution && (
                          <div className="text-[11px] text-slate-400 ml-2.5 italic">{student.institution}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        {student.subscription_tier === "pro" && (
                          <Badge variant="green" className="bg-emerald-500/10 text-emerald-600 border border-emerald-500/20 font-black rounded-lg">
                            ★ চর্চাঘর প্রো
                          </Badge>
                        )}
                        {student.subscription_tier === "max" && (
                          <Badge variant="blue" className="bg-violet-500/10 text-violet-600 border border-violet-500/20 font-black rounded-lg">
                            ★ চর্চাঘর ম্যাক্স
                          </Badge>
                        )}
                        {(!student.subscription_tier || student.subscription_tier === "free") && (
                          <Badge className="bg-gray-100 text-gray-500 border border-gray-200/50 font-bold rounded-lg">
                            ফ্রি / বেসিক
                          </Badge>
                        )}
                        
                        {student.is_blocked ? (
                          <Badge variant="red" className="font-bold rounded-lg">ব্লকড</Badge>
                        ) : (
                          <Badge variant="green" className="font-bold bg-emerald-50 text-emerald-600 border border-emerald-100 rounded-lg">সক্রিয়</Badge>
                        )}
                      </div>
                      <div className="text-[10px] text-gray-400 mt-1.5 ml-1 text-left">
                        কোর্স: {student.course_count}টি এনরোল
                      </div>
                    </td>
                    <td className="px-5 py-4 text-xs text-gray-500 font-bold">
                      {student.registrationDate}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          leftIcon={<Activity className="h-3.5 w-3.5" />}
                          onClick={() => openActivityLogModal(student)}
                          className="rounded-xl text-indigo-600 hover:bg-indigo-50 font-bold text-xs"
                        >
                          কার্যক্রম
                        </Button>
                        <div className="relative">
                          <button
                            onClick={() => setOpenMenuId(openMenuId === student.id ? null : student.id)}
                            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-xl transition-all border border-transparent hover:border-slate-100"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </button>
                          {openMenuId === student.id && (
                            <>
                              <div className="fixed inset-0 z-40" onClick={() => setOpenMenuId(null)} />
                              <div className="absolute right-0 top-full mt-1.5 w-48 bg-white border border-[#E2E8F0] rounded-2xl shadow-xl z-50 py-1.5 animate-in fade-in slide-in-from-top-1 duration-150 text-left">
                                <button
                                  onClick={() => openEditModal(student)}
                                  className="w-full flex items-center gap-2.5 px-4 py-2 text-xs font-bold text-[#374151] hover:bg-slate-50 transition-colors"
                                >
                                  <GraduationCap className="h-4 w-4 text-slate-400" /> সম্পাদন (Edit)
                                </button>
                                <button
                                  onClick={() => openResetPasswordModal(student)}
                                  className="w-full flex items-center gap-2.5 px-4 py-2 text-xs font-bold text-[#374151] hover:bg-slate-50 transition-colors"
                                >
                                  <Key className="h-4 w-4 text-slate-400" /> পাসওয়ার্ড পরিবর্তন
                                </button>
                                <button
                                  onClick={() => { setOpenMenuId(null); handleResetProgress(student); }}
                                  className="w-full flex items-center gap-2.5 px-4 py-2 text-xs font-bold text-[#374151] hover:bg-slate-50 transition-colors"
                                >
                                  <RotateCcw className="h-4 w-4 text-slate-400" /> প্রগ্রেস রিসেট
                                </button>
                                <button
                                  onClick={() => { setOpenMenuId(null); handleToggleBlock(student); }}
                                  className={`w-full flex items-center gap-2.5 px-4 py-2 text-xs font-bold transition-colors ${
                                    student.is_blocked ? "text-emerald-600 hover:bg-emerald-50" : "text-rose-600 hover:bg-rose-50"
                                  }`}
                                >
                                  <UserX className="h-4 w-4" /> {student.is_blocked ? "আনব্লক করুন" : "ব্লক করুন"}
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        {!isLoading && totalPages > 1 && (
          <div className="px-5 py-4 bg-gray-50/50 border-t border-gray-100 flex items-center justify-between">
            <span className="text-xs text-slate-500 font-bold">
              Showing page {currentPage} of {totalPages}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                className="rounded-xl font-bold"
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                className="rounded-xl font-bold"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Edit Student Modal */}
      {editingStudent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity" 
            onClick={() => setEditingStudent(null)} 
          />
          <div className="relative bg-white border border-gray-100 rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 z-10 max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
              <div className="text-left">
                <h2 className="text-base font-black text-gray-900">শিক্ষার্থীর তথ্য সংশোধন</h2>
                <p className="text-xs text-gray-400 mt-1">সম্পাদনা করা হচ্ছে: {editingStudent.name}</p>
              </div>
              <button 
                onClick={() => setEditingStudent(null)}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Body */}
            <form onSubmit={handleUpdateProfile} className="flex-1 overflow-y-auto min-h-0">
              <div className="p-6 space-y-5 text-left">
                {/* Academic Details Section */}
                <div>
                  <h3 className="text-xs font-black text-indigo-600 uppercase tracking-wider mb-3">সাধারণ তথ্য ও সাবস্ক্রিপশন</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                      label="সম্পূর্ণ নাম *"
                      required
                      placeholder="শিক্ষার্থীর নাম লিখুন"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                    />
                    <Input
                      label="মোবাইল নম্বর *"
                      required
                      placeholder="যেমন: 017XXXXXXXX"
                      value={editMobile}
                      onChange={(e) => setEditMobile(e.target.value)}
                    />
                    <Input
                      label="ইমেইল এড্রেস"
                      type="email"
                      placeholder="student@example.com"
                      value={editEmail}
                      onChange={(e) => setEditEmail(e.target.value)}
                    />
                    <div className="flex flex-col gap-1 text-left">
                      <span className="text-xs font-bold text-slate-500 mb-1.5">চর্চাঘর সাবস্ক্রিপশন লেভেল</span>
                      <select
                        value={editSubscription}
                        onChange={(e) => setEditSubscription(e.target.value)}
                        className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 font-bold focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                      >
                        <option value="free">ফ্রি / বেসিক মেম্বার</option>
                        <option value="pro">চর্চাঘর প্রো (Pro Plan)</option>
                        <option value="max">চর্চাঘর ম্যাক্স (Max Plan)</option>
                      </select>
                    </div>
                  </div>
                </div>

                <hr className="border-gray-100" />

                {/* Academic Settings */}
                <div>
                  <h3 className="text-xs font-black text-indigo-600 uppercase tracking-wider mb-3">শিক্ষাগত বিবরণ</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="flex flex-col gap-1 text-left">
                      <span className="text-xs font-bold text-slate-500 mb-1.5">শ্রেণী / ব্যাচ</span>
                      <select
                        value={editClass}
                        onChange={(e) => setEditClass(e.target.value)}
                        className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 font-bold focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                      >
                        <option value="">নির্বাচন করুন</option>
                        <option value="class-6">Class 6 (ষষ্ঠ শ্রেণী)</option>
                        <option value="class-7">Class 7 (সপ্তম শ্রেণী)</option>
                        <option value="class-8">Class 8 (অষ্টম শ্রেণী)</option>
                        <option value="class-9">Class 9 (নবম শ্রেণী)</option>
                        <option value="class-10">Class 10 (দশম শ্রেণী)</option>
                        <option value="hsc-1">HSC 1st Year (এইচএসসি ১ম বর্ষ)</option>
                        <option value="hsc-2">HSC 2nd Year (এইচএসসি ২য় বর্ষ)</option>
                        <option value="ssc-2027">SSC 2027 (এসএসসি ২০২৭)</option>
                        <option value="ssc-2028">SSC 2028 (এসএসসি ২০২৮)</option>
                        <option value="hsc-2026">HSC 2026 (এইচএসসি ২০২৬)</option>
                        <option value="hsc-2027">HSC 2027 (এইচএসসি ২০২৭)</option>
                      </select>
                    </div>

                    <div className="flex flex-col gap-1 text-left">
                      <span className="text-xs font-bold text-slate-500 mb-1.5">বিভাগ (গ্রুপ)</span>
                      <select
                        value={editGroup}
                        onChange={(e) => setEditGroup(e.target.value)}
                        className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 font-bold focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                      >
                        <option value="">নির্বাচন করুন</option>
                        <option value="science">বিজ্ঞান (Science)</option>
                        <option value="humanities">মানবিক (Humanities)</option>
                        <option value="commerce">ব্যবসায় শিক্ষা (Commerce)</option>
                      </select>
                    </div>

                    <div className="flex flex-col gap-1 text-left">
                      <span className="text-xs font-bold text-slate-500 mb-1.5">ঐচ্ছিক বিষয়</span>
                      <select
                        value={editOptional}
                        onChange={(e) => setEditOptional(e.target.value)}
                        className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 font-bold focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                      >
                        <option value="none">কোনোটিই নয়</option>
                        <option value="higher-math">উচ্চতর গণিত (Higher Math)</option>
                        <option value="biology">জীববিজ্ঞান (Biology)</option>
                        <option value="agriculture">কৃষি শিক্ষা (Agriculture)</option>
                        <option value="home-science">গার্হস্থ্য বিজ্ঞান (Home Science)</option>
                      </select>
                    </div>
                  </div>
                </div>

                <hr className="border-gray-100" />

                {/* Profiling and Institution details */}
                <div>
                  <h3 className="text-xs font-black text-indigo-600 uppercase tracking-wider mb-3">প্রতিষ্ঠান ও প্রোফাইলিং তথ্য</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                      label="শিক্ষা প্রতিষ্ঠানের নাম"
                      placeholder="যেমন: ঢাকা কলেজিয়েট স্কুল"
                      value={editInstitution}
                      onChange={(e) => setEditInstitution(e.target.value)}
                    />
                    <Input
                      label="শিক্ষা বোর্ড"
                      placeholder="যেমন: ঢাকা বোর্ড"
                      value={editBoard}
                      onChange={(e) => setEditBoard(e.target.value)}
                    />
                    <Input
                      label="ভবিষ্যৎ লক্ষ্য (Career Goal)"
                      placeholder="যেমন: সফটওয়্যার ইঞ্জিনিয়ার বা ডাক্তার"
                      value={editCareerGoal}
                      onChange={(e) => setEditCareerGoal(e.target.value)}
                    />
                    <Input
                      label="স্বপ্নের বিশ্ববিদ্যালয় (Dream University)"
                      placeholder="যেমন: বুয়েট, ঢাকা বিশ্ববিদ্যালয়"
                      value={editDreamUniversity}
                      onChange={(e) => setEditDreamUniversity(e.target.value)}
                    />
                  </div>
                </div>

                <hr className="border-gray-100" />

                {/* Additional Info */}
                <div>
                  <h3 className="text-xs font-black text-indigo-600 uppercase tracking-wider mb-3">ব্যক্তিগত ও ঠিকানা বিবরণ</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <Input
                      label="জন্ম তারিখ (DOB)"
                      type="date"
                      value={editDob}
                      onChange={(e) => setEditDob(e.target.value)}
                    />
                    <div className="flex flex-col gap-1 text-left">
                      <span className="text-xs font-bold text-slate-500 mb-1.5">লিঙ্গ (Gender)</span>
                      <select
                        value={editGender}
                        onChange={(e) => setEditGender(e.target.value)}
                        className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 font-bold focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                      >
                        <option value="">নির্বাচন করুন</option>
                        <option value="male">পুরুষ (Male)</option>
                        <option value="female">মহিলা (Female)</option>
                        <option value="other">অন্যান্য (Other)</option>
                      </select>
                    </div>
                  </div>
                  <div className="mt-4">
                    <Input
                      label="বর্তমান ঠিকানা"
                      placeholder="সম্পূর্ণ ঠিকানা লিখুন..."
                      value={editAddress}
                      onChange={(e) => setEditAddress(e.target.value)}
                    />
                  </div>
                </div>

                <hr className="border-gray-100" />

                {/* Manual Course Enrolments */}
                <div>
                  <h3 className="text-xs font-black text-indigo-600 uppercase tracking-wider mb-3">কোর্স এনরোলমেন্টস (ম্যানুয়াল)</h3>
                  <Input
                    label="ম্যানুয়াল কোর্স আইডিসমূহ (কমা দিয়ে আলাদা করুন)"
                    placeholder="যেমন: 104, 215, 342"
                    value={editEnrolledCourses}
                    onChange={(e) => setEditEnrolledCourses(e.target.value)}
                  />
                  <p className="text-[10px] text-gray-400 italic mt-1.5">
                    * কুইজ/অধ্যায় ডাটা ও কাস্টম সিলেবাস কোর্স ম্যাপিং সচল করতে কোর্সগুলোর আইডি প্রদান করুন।
                  </p>
                </div>
              </div>
              
              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-3">
                <Button 
                  type="button"
                  variant="outline"
                  onClick={() => setEditingStudent(null)}
                  className="rounded-xl font-bold"
                >
                  বাতিল করুন
                </Button>
                <Button type="submit" className="rounded-xl font-bold">
                  পরিবর্তন সংরক্ষণ করুন
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetPasswordStudent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity" 
            onClick={() => setResetPasswordStudent(null)} 
          />
          <div className="relative bg-white border border-gray-100 rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200 z-10">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
              <h2 className="text-sm font-black text-gray-900 text-left">পাসওয়ার্ড পরিবর্তন করুন</h2>
              <button 
                onClick={() => setResetPasswordStudent(null)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Body */}
            <form onSubmit={handleResetPassword}>
              <div className="p-6 space-y-4 text-left">
                <p className="text-xs text-gray-500">
                  শিক্ষার্থী <strong>{resetPasswordStudent.name}</strong> এর জন্য নতুন পাসওয়ার্ড নির্ধারণ করুন।
                </p>
                <Input
                  label="নতুন পাসওয়ার্ড *"
                  type="password"
                  required
                  placeholder="কমপক্ষে ৬ অক্ষরের পাসওয়ার্ড দিন"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </div>
              
              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-3">
                <Button 
                  type="button"
                  variant="outline"
                  onClick={() => setResetPasswordStudent(null)}
                  className="rounded-xl font-bold"
                >
                  বাতিল
                </Button>
                <Button type="submit" className="rounded-xl font-bold">
                  পাসওয়ার্ড আপডেট করুন
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Activity Log Dashboard Modal */}
      {activeLogStudent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity" 
            onClick={() => setActiveLogStudent(null)} 
          />
          <div className="relative bg-white border border-gray-100 rounded-3xl shadow-2xl w-full max-w-4xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 z-10 h-[85vh] flex flex-col">
            {/* Header */}
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
              <div className="text-left">
                <h3 className="text-base font-black text-gray-900 m-0">কার্যক্রম ও প্রোফাইলিং ড্যাশবোর্ড</h3>
                <p className="text-xs text-gray-400 mt-1">শিক্ষার্থী: <span className="font-bold text-gray-900">{activeLogStudent.name}</span></p>
              </div>
              <button 
                onClick={() => setActiveLogStudent(null)}
                className="text-gray-400 hover:text-gray-600 w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Tab Navigation Header */}
            <div className="px-6 border-b border-gray-100 bg-white flex items-center gap-1.5 overflow-x-auto py-2">
              <button 
                type="button" 
                onClick={() => setActiveLogTab("overview")}
                className={`px-4 py-2 text-xs font-black rounded-xl border border-transparent transition-all hover:bg-gray-50 cursor-pointer ${
                  activeLogTab === "overview" ? "bg-indigo-50 text-indigo-600 border-indigo-200/50" : "text-gray-500"
                }`}
              >
                সারসংক্ষেপ
              </button>
              <button 
                type="button" 
                onClick={() => setActiveLogTab("courses")}
                className={`px-4 py-2 text-xs font-black rounded-xl border border-transparent transition-all hover:bg-gray-50 cursor-pointer ${
                  activeLogTab === "courses" ? "bg-indigo-50 text-indigo-600 border-indigo-200/50" : "text-gray-500"
                }`}
              >
                কোর্সসমূহ ও অগ্রগতি
              </button>
              <button 
                type="button" 
                onClick={() => setActiveLogTab("quizzes")}
                className={`px-4 py-2 text-xs font-black rounded-xl border border-transparent transition-all hover:bg-gray-50 cursor-pointer ${
                  activeLogTab === "quizzes" ? "bg-indigo-50 text-indigo-600 border-indigo-200/50" : "text-gray-500"
                }`}
              >
                এলএমএস কুইজ Attempts
              </button>
              <button 
                type="button" 
                onClick={() => setActiveLogTab("exams")}
                className={`px-4 py-2 text-xs font-black rounded-xl border border-transparent transition-all hover:bg-gray-50 cursor-pointer ${
                  activeLogTab === "exams" ? "bg-indigo-50 text-indigo-600 border-indigo-200/50" : "text-gray-500"
                }`}
              >
                চর্চাঘর পরীক্ষা লগ
              </button>
            </div>

            {/* Body */}
            <div className="p-6 flex-1 overflow-y-auto min-h-0 bg-gray-50/20">
              {activeLogLoading ? (
                <div className="flex flex-col items-center justify-center py-20 gap-3">
                  <span className="animate-spin border-4 border-indigo-500 border-t-transparent rounded-full w-10 h-10"></span>
                  <span className="text-sm font-bold text-gray-500">কার্যক্রম ও প্রোফাইলিং ডাটা লোড হচ্ছে...</span>
                </div>
              ) : activeLogData ? (
                <div className="space-y-6 text-left">
                  
                  {/* OVERVIEW PANEL */}
                  {activeLogTab === "overview" && (
                    <div className="space-y-6">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {/* Total Points */}
                        <div className="p-5 rounded-2xl border border-emerald-100 bg-emerald-500/5 text-left">
                          <span className="text-xs font-bold text-emerald-600 uppercase tracking-wider block">মোট অর্জিত পয়েন্ট</span>
                          <span className="text-3xl font-black text-emerald-700 block mt-2">{activeLogData.overview.points}</span>
                        </div>
                        {/* Rank */}
                        <div className="p-5 rounded-2xl border border-indigo-100 bg-indigo-500/5 text-left">
                          <span className="text-xs font-bold text-indigo-600 uppercase tracking-wider block">লিডারবোর্ড র‍্যাঙ্ক</span>
                          <span className="text-3xl font-black text-indigo-700 block mt-2">#{activeLogData.overview.rank}</span>
                        </div>
                        {/* Streak */}
                        <div className="p-5 rounded-2xl border border-rose-100 bg-rose-50/5 text-left">
                          <span className="text-xs font-bold text-rose-600 uppercase tracking-wider block">ধারাবাহিকতা (Streak)</span>
                          <span className="text-3xl font-black text-rose-700 block mt-2">{activeLogData.overview.streak} দিন</span>
                        </div>
                        {/* Exams Done */}
                        <div className="p-5 rounded-2xl border border-amber-100 bg-amber-500/5 text-left">
                          <span className="text-xs font-bold text-amber-600 uppercase tracking-wider block">অংশগ্রহণকৃত কুইজ</span>
                          <span className="text-3xl font-black text-amber-700 block mt-2">{activeLogData.overview.exams}টি</span>
                        </div>
                        {/* Completed Chapters */}
                        <div className="p-5 rounded-2xl border border-blue-100 bg-blue-500/5 text-left">
                          <span className="text-xs font-bold text-blue-600 uppercase tracking-wider block">সম্পন্নকৃত অধ্যায়</span>
                          <span className="text-3xl font-black text-blue-700 block mt-2">{activeLogData.overview.completed_chapters}টি</span>
                        </div>
                        {/* Favorites */}
                        <div className="p-5 rounded-2xl border border-violet-100 bg-violet-500/5 text-left">
                          <span className="text-xs font-bold text-violet-600 uppercase tracking-wider block">পছন্দের তালিকা (Favorites)</span>
                          <span className="text-3xl font-black text-violet-700 block mt-2">{activeLogData.overview.favorites_count}টি</span>
                        </div>
                      </div>

                      {/* Secondary Details */}
                      <div className="p-5 rounded-2xl border border-gray-200/80 bg-white space-y-3">
                        <h4 className="text-xs font-black text-gray-400 uppercase tracking-wider m-0">অতিরিক্ত বিবরণ</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mt-3 pt-3 border-t border-gray-100">
                          <div>
                            <span className="text-gray-400">নিবন্ধনের তারিখ:</span>
                            <span className="font-bold text-gray-700 ml-1">
                              {new Date(activeLogData.user.registered).toLocaleDateString('bn-BD', { year: 'numeric', month: 'long', day: 'numeric' })}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-400">সক্রিয় সাবস্ক্রিপশন:</span>
                            <span className={`font-black ml-1 ${
                              activeLogData.user.subscription === "pro" ? "text-emerald-600" :
                              activeLogData.user.subscription === "max" ? "text-violet-600" : "text-gray-500"
                            }`}>
                              {activeLogData.user.subscription === "pro" ? "★ চর্চাঘর প্রো" :
                               activeLogData.user.subscription === "max" ? "★ চর্চাঘর ম্যাক্স" : "ফ্রি / বেসিক"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* COURSES PROGRESS PANEL */}
                  {activeLogTab === "courses" && (
                    <div className="space-y-4">
                      {activeLogData.courses && activeLogData.courses.length > 0 ? (
                        activeLogData.courses.map((c: any) => (
                          <div key={c.id} className="p-4 bg-white border border-gray-200/80 rounded-2xl flex flex-col gap-3">
                            <div className="flex items-center justify-between">
                              <span className="font-bold text-gray-900 text-sm">{c.title}</span>
                              <span className="text-xs font-bold text-indigo-600 bg-indigo-50 px-2.5 py-0.5 rounded-full">{c.percent}% সম্পন্ন</span>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                              <div className="bg-indigo-600 h-2 rounded-full" style={{ width: `${c.percent}%` }}></div>
                            </div>
                            <div className="flex items-center justify-between text-xs text-gray-400">
                              <span>এনরোলমেন্ট: {new Date(c.enrol_date).toLocaleDateString('bn-BD')}</span>
                              <span>লেসন: {c.completed_lessons}/{c.total_lessons}টি সম্পন্ন</span>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="p-8 bg-white border border-gray-200/80 rounded-2xl text-center text-gray-400 text-xs font-bold">
                          কোনো কোর্সে এনরোলমেন্ট তথ্য পাওয়া যায়নি।
                        </div>
                      )}
                    </div>
                  )}

                  {/* TUTOR QUIZZES PANEL */}
                  {activeLogTab === "quizzes" && (
                    <div className="overflow-x-auto border border-gray-200/80 rounded-2xl bg-white">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-100">
                            <th className="px-5 py-3 text-xs font-bold text-gray-500 uppercase">কুইজ টাইটেল</th>
                            <th className="px-5 py-3 text-xs font-bold text-gray-500 uppercase text-center w-24">মোট প্রশ্ন</th>
                            <th className="px-5 py-3 text-xs font-bold text-gray-500 uppercase text-center w-24">উত্তরকৃত</th>
                            <th className="px-5 py-3 text-xs font-bold text-gray-500 uppercase text-center w-28">প্রাপ্ত নম্বর</th>
                            <th className="px-5 py-3 text-xs font-bold text-gray-500 uppercase text-center w-24">ফলাফল</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 text-sm">
                          {activeLogData.quiz_attempts && activeLogData.quiz_attempts.length > 0 ? (
                            activeLogData.quiz_attempts.map((q: any) => (
                              <tr key={q.id}>
                                <td className="px-5 py-3 text-gray-900 font-bold">{q.title}</td>
                                <td className="px-5 py-3 text-center text-gray-500 font-mono">{q.total_questions}</td>
                                <td className="px-5 py-3 text-center text-gray-500 font-mono">{q.total_answers}</td>
                                <td className="px-5 py-3 text-center text-indigo-600 font-black font-mono">{q.earned_marks}</td>
                                <td className="px-5 py-3 text-center">
                                  <span className={`px-2.5 py-0.5 rounded-lg text-[10px] font-bold ${
                                    q.status === "pass" || q.status === "passed" || q.status === "Pass" ?
                                    "text-emerald-600 bg-emerald-50 border border-emerald-200/50" :
                                    "text-rose-600 bg-rose-50 border border-rose-200/50"
                                  }`}>
                                    {q.status === "pass" || q.status === "passed" || q.status === "Pass" ? "উত্তীর্ণ" : "ফেইল"}
                                  </span>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={5} className="px-5 py-8 text-center text-gray-400 text-xs font-bold">
                                কোনো কুইজ প্রচেষ্টা পাওয়া যায়নি।
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* CHORCHAGHOR EXAMS PANEL */}
                  {activeLogTab === "exams" && (
                    <div className="overflow-x-auto border border-gray-200/80 rounded-2xl bg-white">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-100">
                            <th className="px-5 py-3 text-xs font-bold text-gray-500 uppercase">বিষয় / অধ্যায়</th>
                            <th className="px-5 py-3 text-xs font-bold text-gray-500 uppercase w-28">পরীক্ষার ধরন</th>
                            <th className="px-5 py-3 text-xs font-bold text-gray-500 uppercase text-center w-28">প্রাপ্ত নম্বর</th>
                            <th className="px-5 py-3 text-xs font-bold text-gray-500 uppercase text-center w-28">নেগেটিভ নম্বর</th>
                            <th className="px-5 py-3 text-xs font-bold text-gray-500 uppercase text-center w-28">সময় (সেকেন্ড)</th>
                            <th className="px-5 py-3 text-xs font-bold text-gray-500 uppercase text-right w-40">তারিখ ও সময়</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 text-sm">
                          {activeLogData.chorcha_attempts && activeLogData.chorcha_attempts.length > 0 ? (
                            activeLogData.chorcha_attempts.map((e: any) => (
                              <tr key={e.id}>
                                <td className="px-5 py-3 text-gray-900 font-bold">{e.subject}</td>
                                <td className="px-5 py-3 text-gray-500">
                                  {e.quiz_type === "mcq" ? "বহুনির্বাচনী" : e.quiz_type === "exam" ? "মডেল টেস্ট" : e.quiz_type}
                                </td>
                                <td className="px-5 py-3 text-center text-emerald-600 font-black font-mono">{e.score}</td>
                                <td className="px-5 py-3 text-center text-rose-500 font-bold font-mono">{e.negative}</td>
                                <td className="px-5 py-3 text-center text-gray-500 font-mono">{e.time_taken}</td>
                                <td className="px-5 py-3 text-right text-gray-400 text-xs font-mono">
                                  {new Date(e.date).toLocaleString('bn-BD', { year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={6} className="px-5 py-8 text-center text-gray-400 text-xs font-bold">
                                কোনো পরীক্ষার রেকর্ড পাওয়া যায়নি।
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}

                </div>
              ) : (
                <div className="text-center py-20 text-gray-400 text-xs font-bold">
                  ডাটা লোড করা যায়নি।
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end bg-gray-50/50">
              <Button 
                onClick={() => setActiveLogStudent(null)}
                className="rounded-xl font-bold bg-gray-900 hover:bg-black text-white text-xs"
              >
                বন্ধ করুন
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
